import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { LiberacaoCondicaoResponse, LiberacaoItemResponse, LiberacaoTabelaResponse, OrigemPraticado, RascunhoItemDto } from '@triade/contracts';
import { ChevronRight, Send } from 'lucide-react';
import { useGravarRascunho, useLiberacaoItem, usePublicar, useRascunho } from '@/api/liberacao';
import { usePode } from '@/api/me';
import { AcoesLote } from '@/components/liberacao/acoes-lote';
import { PublicarDialog } from '@/components/lista-precos/rascunho-publicacao';
import { FaixaLucroBadge, ProfitBadge } from '@/components/profit-badge';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { decimalDigitado, decimalParaTextoCampo, formatarContagem, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ErroFormacao } from './avisos-formacao';
import { ehErroTributosAusentes, ehIndisponivelTributos, LinkCadastrarTributos } from '@/components/item-cadastro/link-cadastrar-tributos';
import { EscopoItem } from './escopo-item';
import { PainelReversoDialog } from './painel-reverso';

const ROTULO_ORIGEM: Record<OrigemPraticado, string> = { BASE: 'base', MANUAL: 'manual', DERIVADO: 'derivado', DIRETO: 'direto' };
const ESTILO_ORIGEM: Record<OrigemPraticado, string> = {
  BASE: 'bg-secondary text-secondary-foreground',
  MANUAL: 'bg-warning-bg text-warning-ink',
  DERIVADO: 'bg-secondary text-ink-muted',
  DIRETO: 'bg-secondary text-ink-muted',
};
const CHIP = 'rounded-full px-2 text-caption leading-5 font-medium whitespace-nowrap';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

export interface ItemLiberacao {
  id: string;
  codigo: string;
  descricao: string;
}

type Gravar = (linha: { tabela: LiberacaoTabelaResponse; condicao: LiberacaoCondicaoResponse; preco: string | null; manual: boolean }) => Promise<void>;

/**
 * Formacao: um item x todas as tabelas. Mostra, por condicao, o publicado, o rascunho, o efetivo (o que vale se publicar
 * agora), o lucro e o sugerido, tudo da API. Edita o preco base (condicao BASE) ou fixa um preco manual nas demais.
 * Nada e calculado aqui: derivacao, lucro e faixa vem de GET /precificacao/liberacao/item/:id.
 */
export function LiberacaoItem({ item }: { item: ItemLiberacao }) {
  const liberacao = useLiberacaoItem(item.id);
  // "Preco pelo resultado" por celula: mesma gate da Formacao (simular + custo.ver).
  const { pode } = usePode();
  const podeReverso = pode('precificacao.simular') && pode('custo.ver');
  const gravar = useGravarRascunho();
  const rascunho = useRascunho();
  const publicar = usePublicar();
  const [publicarAberto, setPublicarAberto] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const total = rascunho.data?.totalPrecos ?? 0;

  const onGravar: Gravar = async ({ tabela, condicao, preco, manual }) => {
    setErro(null);
    setAviso(null);
    const linha: RascunhoItemDto = { itemId: item.id, tabelaId: tabela.tabelaId, condicaoId: condicao.condicaoId, preco, manual };
    try {
      await gravar.mutateAsync({ itens: [linha] });
      setAviso(preco === null ? `${condicao.condicaoNome}: voltou a derivar da base (rascunho)` : 'Salvo no rascunho (não publicado)');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  };

  if (liberacao.isError)
    return (
      <ErroFormacao
        erro={liberacao.error}
        onTentar={() => void liberacao.refetch()}
        acao={ehErroTributosAusentes(liberacao.error) ? <LinkCadastrarTributos itemId={item.id} /> : undefined}
      />
    );
  if (!liberacao.data) return <p role="status" className="rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center text-small text-ink-muted">Carregando liberação de preço…</p>;

  const dados: LiberacaoItemResponse = liberacao.data;

  return (
    <section aria-label="Liberação de preço" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5" data-testid="liberacao-item">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-title font-semibold">Liberação de preço — todas as tabelas</h2>
          <p className="mt-1 text-small text-ink-secondary">
            {dados.itemCodigo} · {dados.itemDescricao}. Digite o preço da tabela: ele vale na condição padrão e as outras condições são calculadas. Preço
            preenchido = item liberado. Edite no rascunho; só vale depois de publicar a lista.
          </p>
        </div>
        <AcoesLote
          escopo={{ itemIds: [item.id] }}
          rotuloEscopo={`${dados.itemCodigo}, todas as tabelas`}
          excecoes={<EscopoItem itemId={item.id} tabelas={dados.tabelas} />}
        />
      </div>

      {erro && (
        <p role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          Não foi possível salvar no rascunho: {erro}
        </p>
      )}
      {aviso && (
        <p role="status" className="text-small text-ink-secondary">
          {aviso}
        </p>
      )}

      {dados.tabelas.length === 0 && (
        <p className="text-small text-ink-muted">
          Nenhuma tabela de preço ativa. Crie em{' '}
          <Link to="/cadastros/tabelas" className="font-medium text-primary hover:underline">
            Cadastros › Tabelas de preço
          </Link>
          .
        </p>
      )}

      {dados.tabelas.map((t) => (
        <TabelaLiberacao key={t.tabelaId} itemId={item.id} tabela={t} onGravar={onGravar} pendente={gravar.isPending} podeReverso={podeReverso} />
      ))}

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
        <span className="text-small text-ink-secondary">
          {total > 0 ? `Rascunho: ${formatarContagem(total)} ${total === 1 ? 'preço' : 'preços'} não publicados` : 'Nada no rascunho.'}
        </span>
        <span className="flex flex-wrap items-center gap-3">
          <Button type="button" size="sm" disabled={total === 0} onClick={() => { setAviso(null); publicar.reset(); setPublicarAberto(true); }}>
            <Send aria-hidden="true" className="size-4" />
            Publicar lista…
          </Button>
          <Link to="/precificar/lista" className="text-small font-medium text-primary hover:underline">
            Ver rascunho na Lista
          </Link>
        </span>
      </footer>
      <PublicarDialog
        aberto={publicarAberto}
        onAberto={setPublicarAberto}
        total={total}
        pendente={publicar.isPending}
        onPublicar={async (dto) => {
          const r = await publicar.mutateAsync(dto);
          setAviso(
            `Lista publicada: ${formatarContagem(r.totalPrecos)} ${r.totalPrecos === 1 ? 'preço' : 'preços'} (${formatarContagem(r.marcasResolvidas)} ${r.marcasResolvidas === 1 ? 'marca de revisão resolvida' : 'marcas de revisão resolvidas'})`,
          );
        }}
      />
    </section>
  );
}

function TabelaLiberacao({
  itemId,
  tabela,
  onGravar,
  pendente,
  podeReverso,
}: {
  itemId: string;
  tabela: LiberacaoTabelaResponse;
  onGravar: Gravar;
  pendente: boolean;
  podeReverso: boolean;
}) {
  const lucroDesejado = tabela.lucroDesejado;
  // Recolhida por padrao: as condicoes so aparecem ao abrir; os avisos por condicao ficam sempre na linha da tabela.
  const [aberta, setAberta] = useState(false);
  const base = tabela.condicoes.find((c) => c.base) ?? null;
  const abaixoDaMeta = tabela.condicoes.filter((c) => c.faixa === 'orange').length;
  const abaixoDoMinimo = tabela.condicoes.filter((c) => c.faixa === 'red').length;
  const idCorpo = `lib-corpo-${tabela.tabelaId}`;
  return (
    <div aria-label={`Tabela ${tabela.tabelaNome}`} role="group" className="flex flex-col gap-2 rounded-xl border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          aria-expanded={aberta}
          aria-controls={idCorpo}
          onClick={() => setAberta((v) => !v)}
          className="flex items-center gap-1.5 rounded-md text-left outline-none focus-visible:shadow-focus"
        >
          <ChevronRight aria-hidden="true" className={cn('size-4 shrink-0 transition-transform', aberta && 'rotate-90')} />
          <h3 className="text-body font-semibold">
            {tabela.tabelaNome} <span className="font-normal text-ink-muted">· {tabela.tabelaCodigo}</span>
          </h3>
          <span className="text-caption font-normal text-ink-muted">{aberta ? 'Ocultar condições' : 'Mostrar condições'}</span>
        </button>
        <span className="text-caption text-ink-muted">
          Lucro desejado {lucroDesejado ? formatarFracaoPercentual(lucroDesejado) : 'não definido'} · mínimo {formatarFracaoPercentual(tabela.lucroMinimo)}
        </span>
      </div>

      {!tabela.indisponivel && tabela.condicoes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="avisos-tabela">
          {abaixoDoMinimo > 0 && (
            <span className={cn(CHIP, 'bg-danger-bg text-danger-ink')}>
              {abaixoDoMinimo} {abaixoDoMinimo === 1 ? 'condição abaixo do mínimo' : 'condições abaixo do mínimo'}
            </span>
          )}
          {abaixoDaMeta > 0 && (
            <span className={cn(CHIP, 'bg-warning-bg text-warning-ink')}>
              {abaixoDaMeta} {abaixoDaMeta === 1 ? 'condição abaixo da meta' : 'condições abaixo da meta'}
            </span>
          )}
          {abaixoDoMinimo === 0 && abaixoDaMeta === 0 && <span className="text-caption text-ink-muted">Nenhuma condição abaixo da meta.</span>}
        </div>
      )}

      {tabela.indisponivel ? (
        <p role="status" className="rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
          {tabela.indisponivel.message} ({tabela.indisponivel.code}){' '}
          {ehIndisponivelTributos(tabela.indisponivel) && <LinkCadastrarTributos itemId={itemId} />}
        </p>
      ) : (
        <>
          {base && <PrecoDaTabela itemId={itemId} tabela={tabela} base={base} onGravar={onGravar} pendente={pendente} podeReverso={podeReverso} />}
          {aberta && (
            <div id={idCorpo} className="overflow-x-auto">
              <table className="w-full min-w-215 border-collapse text-small">
                <thead>
                  <tr className="border-b border-border text-left text-caption text-ink-muted">
                    {['Condição', 'Publicado', 'Rascunho', 'Efetivo', 'Lucro', 'Sugerido', 'Edição'].map((c) => (
                      <th key={c} scope="col" className="px-2 py-2 font-medium">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tabela.condicoes.map((c) => (
                    <LinhaCondicao key={c.condicaoId} itemId={itemId} tabela={tabela} condicao={c} onGravar={onGravar} pendente={pendente} podeReverso={podeReverso} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Preco digitado na tabela: grava na condicao padrao (base); as demais condicoes derivam pelo fator financeiro (calculo na API). */
function PrecoDaTabela({
  itemId,
  tabela,
  base,
  onGravar,
  pendente,
  podeReverso,
}: {
  itemId: string;
  tabela: LiberacaoTabelaResponse;
  base: LiberacaoCondicaoResponse;
  onGravar: Gravar;
  pendente: boolean;
  podeReverso: boolean;
}) {
  const [texto, setTexto] = useState(() => decimalParaTextoCampo(base.rascunho));
  const [erroCampo, setErroCampo] = useState<string | null>(null);
  useEffect(() => setTexto(decimalParaTextoCampo(base.rascunho)), [base.rascunho]);
  const idCampo = `lib-${tabela.tabelaId}-${base.condicaoId}`;
  const dataHoje = new Date().toLocaleDateString('en-CA');

  function salvar() {
    const preco = decimalDigitado(texto);
    if (preco === null) {
      setErroCampo('Informe um valor como 25,00.');
      return;
    }
    setErroCampo(null);
    void onGravar({ tabela, condicao: base, preco, manual: false });
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="preco-da-tabela">
      <label htmlFor={idCampo} className="text-small font-medium">
        Preço da tabela <span className="font-normal text-ink-muted">(condição padrão: {base.condicaoNome})</span>
      </label>
      <input
        id={idCampo}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-label={`Preço base ${base.condicaoNome}`}
        placeholder="0,00"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        aria-invalid={erroCampo ? true : undefined}
        className="tabular h-8 w-28 rounded-md border border-input bg-card px-2 outline-none focus-visible:border-primary focus-visible:shadow-focus"
      />
      <Button type="button" size="sm" variant="outline" disabled={pendente} onClick={salvar}>
        Salvar no rascunho
      </Button>
      {podeReverso && (
        <PainelReversoDialog
          referencia={{ itemId, tabelaId: tabela.tabelaId, condicaoId: base.condicaoId, data: dataHoje }}
          podeUsar
          rotuloUsar="Usar nesta condição"
          onUsarComoPraticado={(preco) => {
            setErroCampo(null);
            setTexto(decimalParaTextoCampo(preco));
          }}
          idCampoRetorno={idCampo}
        />
      )}
      {erroCampo && (
        <p role="alert" className="basis-full text-caption text-danger-ink">
          {erroCampo}
        </p>
      )}
    </div>
  );
}

function Origem({ origem }: { origem: OrigemPraticado | null }) {
  if (!origem) return null;
  return <span className={cn(CHIP, 'ml-1.5', ESTILO_ORIGEM[origem])}>{ROTULO_ORIGEM[origem]}</span>;
}

function LinhaCondicao({
  itemId,
  tabela,
  condicao: c,
  onGravar,
  pendente,
  podeReverso,
}: {
  itemId: string;
  tabela: LiberacaoTabelaResponse;
  condicao: LiberacaoCondicaoResponse;
  onGravar: Gravar;
  pendente: boolean;
  podeReverso: boolean;
}) {
  const [texto, setTexto] = useState(() => decimalParaTextoCampo(c.rascunho));
  const [erroCampo, setErroCampo] = useState<string | null>(null);
  const navigate = useNavigate();
  // Depois de gravar (ou de outra acao na tela), o campo volta a refletir o rascunho da API.
  useEffect(() => setTexto(decimalParaTextoCampo(c.rascunho)), [c.rascunho]);

  const ehBase = c.base;
  const rotuloCampo = ehBase ? `Preço base ${c.condicaoNome}` : `Fixar preço ${c.condicaoNome}`;
  const idCampo = `lib-${tabela.tabelaId}-${c.condicaoId}`;
  // Data da referencia do "Preco pelo resultado": hoje (data civil local, so formato).
  const dataHoje = new Date().toLocaleDateString('en-CA');

  // Carrega a combinacao na Formacao (item, tabela, condicao) com o preco efetivo da celula como praticado.
  function carregarNaSimulacao() {
    void navigate({
      to: '/precificar/formacao',
      search: {
        itemId,
        tabelaId: tabela.tabelaId,
        condicaoId: c.condicaoId,
        ...(c.efetivo !== null ? { praticado: c.efetivo } : {}),
      },
    });
  }

  function salvar() {
    const preco = decimalDigitado(texto);
    if (preco === null) {
      setErroCampo('Informe um valor como 25,00.');
      return;
    }
    setErroCampo(null);
    void onGravar({ tabela, condicao: c, preco, manual: !ehBase });
  }

  return (
    <tr data-testid="linha-condicao" data-base={ehBase ? 'true' : undefined} className="border-b border-border align-top">
      <td className="px-2 py-2">
        <span className="font-medium">{c.condicaoNome}</span>
        {ehBase && <span className={cn(CHIP, 'ml-1.5 bg-primary/10 text-primary')}>base</span>}
      </td>
      <td className="tabular px-2 py-2">
        <span>{formatarMoeda(c.publicado)}</span>
        <Origem origem={c.publicadoOrigem} />
      </td>
      <td className="tabular px-2 py-2">{c.rascunho === null ? <span className="text-ink-disabled">—</span> : formatarMoeda(c.rascunho)}</td>
      <td className="tabular px-2 py-2 font-semibold">
        <span>{formatarMoeda(c.efetivo)}</span>
        <Origem origem={c.efetivoOrigem} />
      </td>
      <td className="px-2 py-2">
        {c.lucro === null ? (
          <span className="text-ink-muted">—</span>
        ) : tabela.lucroDesejado ? (
          <ProfitBadge compacto lucro={c.lucro} meta={tabela.lucroDesejado} minimo={tabela.lucroMinimo} faixa={c.faixa ?? undefined} />
        ) : (
          <span className="flex items-center gap-1.5">
            <span className="tabular">{formatarFracaoPercentual(c.lucro)}</span>
            {c.faixa && <FaixaLucroBadge faixa={c.faixa} />}
          </span>
        )}
      </td>
      <td className="tabular px-2 py-2">{formatarMoeda(c.sugerido)}</td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {ehBase ? (
            <span className="text-caption text-ink-muted">Digite o preço no topo da tabela.</span>
          ) : (
            <>
              <input
                id={idCampo}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                aria-label={rotuloCampo}
                placeholder="fixar (manual)"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                aria-invalid={erroCampo ? true : undefined}
                className="tabular h-8 w-28 rounded-md border border-input bg-card px-2 outline-none focus-visible:border-primary focus-visible:shadow-focus"
              />
              <Button type="button" size="sm" variant="outline" disabled={pendente} onClick={salvar}>
                Salvar no rascunho
              </Button>
              {podeReverso && (
                <PainelReversoDialog
                  referencia={{ itemId, tabelaId: tabela.tabelaId, condicaoId: c.condicaoId, data: dataHoje }}
                  podeUsar
                  rotuloUsar="Usar nesta condição"
                  onUsarComoPraticado={(preco) => {
                    setErroCampo(null);
                    setTexto(decimalParaTextoCampo(preco));
                  }}
                  idCampoRetorno={idCampo}
                />
              )}
            </>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={carregarNaSimulacao}>
            Carregar na simulação
          </Button>
          {!ehBase && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={pendente || c.efetivoOrigem !== 'MANUAL'}
              onClick={() => {
                setErroCampo(null);
                void onGravar({ tabela, condicao: c, preco: null, manual: false });
              }}
            >
              voltar a derivar
            </Button>
          )}
        </div>
        {erroCampo && (
          <p role="alert" className="mt-1 text-caption text-danger-ink">
            {erroCampo}
          </p>
        )}
      </td>
    </tr>
  );
}
